import { useEffect, useRef } from "react";
import { useMutation, useConvexAuth } from "convex/react";
import { useSession } from "@/hooks/use-session";
import * as Sentry from "@sentry/react";

/**
 * Ensure the authenticated WorkOS user has a row in Convex `users`.
 * - Runs only after Convex auth is established
 * - Idempotent and re-runs when the authenticated user changes
 */
export function useEnsureDbUser() {
  const { user } = useSession();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const ensureUser = useMutation("users:ensureUser" as any);
  const lastEnsuredUserIdRef = useRef<string | null>(null);

  // Reset cache on logout so we re-run for the next login in the same session
  useEffect(() => {
    if (!isAuthenticated) {
      lastEnsuredUserIdRef.current = null;
      Sentry.setUser(null); // Clear Sentry user on logout
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated && user) {
      throw new Error("Not authenticated");
    }

    if (!isAuthenticated) {
      return;
    }

    if (!user) {
      return;
    }

    // Only (re)ensure when the authenticated WorkOS user changes.
    if (lastEnsuredUserIdRef.current === user.id) return;

    // Pass user profile data from session to Convex
    // (JWT doesn't contain email/name, so we pass them explicitly)
    const userName = user.firstName
      ? `${user.firstName} ${user.lastName || ""}`.trim()
      : undefined;

    ensureUser({ email: user.email, name: userName })
      .then(() => {
        lastEnsuredUserIdRef.current = user.id;
        // Set Sentry user context for error tracking
        Sentry.setUser({ id: user.id, email: user.email });
      })
      .catch((err: unknown) => {
        console.error("[auth] ensureUser failed", err);
        // allow retry next effect pass
        lastEnsuredUserIdRef.current = null;
      });
  }, [isAuthenticated, isLoading, user, ensureUser]);
}
